import { config } from "@/config";
import { z, ZodObject, ZodRawShape } from "zod";
import { AppInfo } from "@/types/v2/App";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Button } from "@/components/Base/Button";
import { Input } from "@/components/Base/Input";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { Tooltip } from "@/components/Common/Tooltip";

const shape: ZodRawShape = {
  liveVersion: z.string().min(1, { message: "Live version is required" }),
  previewVersion: z.string().min(1, { message: "Preview version is required" }),
};

const formSchema: ZodObject<typeof shape> = z.object({
  ...shape,
});

const initialValues: z.infer<typeof formSchema> = {
  liveVersion: config.version,
  previewVersion: "",
};

type AppVersionFormProps = {
  data?: AppInfo;
  onSubmit: (values: z.infer<typeof formSchema>) => void;
};

export const AppVersionForm = ({ data, onSubmit }: AppVersionFormProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { ...initialValues, ...data },
  });

  const handleApplyLiveVersion = () => {
    form.setValue("liveVersion", config.version);
  };

  const handleApplyPreviewVersion = () => {
    form.setValue("previewVersion", config.version);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-row items-end gap-2">
          <FormField
            render={({ field }) => (
              <FormItem>
                <FormLabel>Live Version</FormLabel>
                <FormControl>
                  <Input
                    className={"text-primary"}
                    type="text"
                    placeholder="Live Version"
                    required={true}
                    autoFocus={true}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
            name="liveVersion"
            control={form.control}
          />
          <Tooltip message="Apply Current App Version" asChild={true}>
            <Button onClick={handleApplyLiveVersion}>
              <Icon name={IconNames.upload} />
            </Button>
          </Tooltip>
        </div>

        <div className="flex flex-row items-end gap-2">
          <FormField
            render={({ field }) => (
              <FormItem>
                <FormLabel>Preview Version</FormLabel>
                <FormControl>
                  <Input
                    className={"text-primary"}
                    type="text"
                    placeholder="Preview Version"
                    required={true}
                    autoFocus={true}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
            name="previewVersion"
            control={form.control}
          />

          <Tooltip message="Apply Current App Version" asChild={true}>
            <Button onClick={handleApplyPreviewVersion}>
              <Icon name={IconNames.upload} />
            </Button>
          </Tooltip>
        </div>
        <div className="mt-4 flex flex-row items-end justify-end gap-2">
          <Button type={"submit"} variant={"accent"}>
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};
